import { Divider, Flex, Box, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { getUnitIconByUniqueValueKey, getUnitUniqueValueKey } from '../helpers';
import { EsriMapEntityTypeEnum } from '../../../../esri/types';
import { getUnitStateId } from '../../../../esri/helpers/map-icon-theme';
import { CloseMapLedgerAndClearSelectedEntityButton } from '../close-map-ledger-and-clear-selected-entity-button';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { getOneSizeSmaller } from '../../../../common/helpers/utils';
import { FR_ICON_SIZE, ICON_SIZE } from '../constants';

export const UnitOverview = observer(function UnitOverview(): React.ReactElement {
    const { isFirstResponder } = useApplicationType();
    const mapStore = useEsriMapStore();
    const selectedMapLedgerUnit = mapStore.selectedMapLedgerUnit;
    const selectedEntityType = mapStore.selectedMapLedgerEntityType;

    const size = isFirstResponder ? 'lg' : 'md';
    const iconSize = isFirstResponder ? FR_ICON_SIZE : ICON_SIZE;

    const { entityId, callSign, personnel } = selectedMapLedgerUnit || {};

    const mapViewportUnit = entityId ? mapStore.getMapViewportUnit(entityId) : undefined;
    const isUser = selectedEntityType === EsriMapEntityTypeEnum.USER;

    let isLocationPotentiallyInaccurate = mapViewportUnit?.isLocationPotentiallyInaccurate;
    let userNameDisplay;

    if (isUser) {
        const selectedUnitUserId = mapStore.selectedUnitUserId;
        const unitUserLocation = mapViewportUnit?.unitUserLocations?.find(
            (unitUser) => unitUser.entityId === selectedUnitUserId
        );
        isLocationPotentiallyInaccurate = unitUserLocation?.isLocationPotentiallyInaccurate;
        userNameDisplay = unitUserLocation?.userNameDisplay;
    }

    const unitUniqueValueKey = getUnitUniqueValueKey({
        selectedMapLedgerUnit,
        isLocationPotentiallyInaccurate,
        isUser,
    });
    const unitOrUserIcon = getUnitIconByUniqueValueKey(
        unitUniqueValueKey,
        getUnitStateId(mapViewportUnit ?? selectedMapLedgerUnit)
    );

    const smallerSize = getOneSizeSmaller(size);

    return (
        <Box data-testid="MAP_UNIT_OVERVIEW_SECTION">
            <Flex alignItems="center" justifyContent="space-between" padding="12px 18px">
                <Flex gap={2}>
                    {!!unitOrUserIcon && (
                        <Flex alignItems="center" width={iconSize} minWidth={iconSize}>
                            <img
                                src={unitOrUserIcon}
                                alt="icon"
                                width={iconSize}
                                height={iconSize}
                                data-icon-key={unitUniqueValueKey}
                                data-testid="MAP_UNIT_USER_ICON"
                            />
                        </Flex>
                    )}
                    <Flex direction="column" justifyContent={!personnel ? 'center' : 'unset'}>
                        <Text fontWeight="bold" style={{ wordBreak: 'break-word' }} fontSize={size}>
                            {selectedEntityType === EsriMapEntityTypeEnum.UNIT
                                ? `Unit ${callSign}`
                                : callSign}
                        </Text>
                        {personnel && selectedEntityType === EsriMapEntityTypeEnum.UNIT && (
                            <Text fontSize={smallerSize} style={{ wordBreak: 'break-word' }}>
                                {personnel}
                            </Text>
                        )}
                        {userNameDisplay && selectedEntityType === EsriMapEntityTypeEnum.USER && (
                            <Text fontSize={smallerSize} style={{ wordBreak: 'break-word' }}>
                                {userNameDisplay}
                            </Text>
                        )}
                    </Flex>
                </Flex>
                <CloseMapLedgerAndClearSelectedEntityButton />
            </Flex>
            <Divider />
        </Box>
    );
});
